// Ekran 04 — "Firma 360° analiz" tam sayfa sekmesi.
// ref_files/btys-erp-04-cariler.html içindeki fullHTML() / openFull() / pasifeAl()
// fonksiyonlarının karşılığı (:572-617).
//
// Yerleşim referansla birebir:
//   .crumb → .d-head → .kstrip(5) → .d-grid[ sol: Kalite Karnesi + İşlem Akışı
//                                    | sağ: Rol Yönetimi + Cari Ekstre + Araçlar ]
// Dış sarmalayıcı (.pane) SekmePanolari tarafından basılır, burada tekrarlanmaz.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trTarih } from '../../lib/trFormat'
import { useYetki } from '../../lib/yetki'
import { cariler, type Cari } from './api'
import {
  ROLLER,
  RolAnahtarlari,
  RolRozetleri,
  sapmaMetni,
  sapmaSinifi,
  sapmaVurgusu,
  type RolAnahtar,
} from './CariOrtak'

export default function CariAnaliziSekmesi({ id }: { id: string }) {
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('FiloCariler', 'yaz')

  // Liste sayfasıyla aynı sorgu anahtarı: panelde yapılan rol değişikliği
  // sekmeye, sekmedeki durum değişikliği panele anında yansısın.
  const { data: detay } = useQuery({ queryKey: ['cari', id], queryFn: () => cariler.detay(id) })

  const kaydet = useMutation({
    // Toast metni çağırana bırakılıyor: rol değişikliği ile pasife alma aynı
    // ucu kullanıyor ama kullanıcıya farklı yan etkiyi söylemeleri gerekiyor.
    mutationFn: (v: { govde: Partial<Cari>; mesaj: (yeni: Cari) => string }) =>
      cariler.kaydet(id, v.govde),
    onSuccess: (yeni, v) => {
      toast(v.mesaj(yeni))
      void kuyruk.invalidateQueries({ queryKey: ['cari', id] })
      void kuyruk.invalidateQueries({ queryKey: ['cariler'] })
      void kuyruk.invalidateQueries({ queryKey: ['cari-ozeti'] })
    },
    onError: (e) => toast(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.'),
  })

  if (!detay) return <div className="crumb">Yükleniyor…</div>

  const c = detay.cari
  const pasif = c.durum === 'pasif'
  const kilitli = !yazabilir || kaydet.isPending
  const kilitNedeni = !yazabilir ? 'Bu modülde yazma yetkiniz yok' : undefined

  function rolDegistir(anahtar: RolAnahtar) {
    if (kilitli) return
    // "En az bir rol" kuralını sunucu 409 ile zorluyor; kullanıcıyı boşuna
    // sunucuya göndermemek için son rolü burada da tutuyoruz (referans da böyle).
    const acikSayisi = ROLLER.filter((r) => c[r.anahtar]).length
    if (c[anahtar] && acikSayisi === 1) {
      toast('Firma en az bir rol taşımalı')
      return
    }
    const roller = {
      rolAtik: c.rolAtik,
      rolTedarikci: c.rolTedarikci,
      rolNakliyeci: c.rolNakliyeci,
    }
    roller[anahtar] = !c[anahtar]

    kaydet.mutate({
      govde: { ...c, ...roller },
      mesaj: (y) =>
        `${y.unvan} rolleri güncellendi: ${ROLLER.filter((r) => y[r.anahtar])
          .map((r) => r.etiket)
          .join(' + ')}`,
    })
  }

  /** Referans `pasifeAl()` — durum alanı sunucuda mevcut (CariIstek.Durum). */
  function durumDegistir() {
    if (kilitli) return
    kaydet.mutate({
      govde: { ...c, durum: pasif ? 'aktif' : 'pasif' },
      mesaj: (y) => `${y.unvan} ${y.durum === 'pasif' ? 'pasife alındı' : 'aktife alındı'}`,
    })
  }

  return (
    <>
      <div className="crumb">
        Ana Veri / Cariler <b>/ {c.unvan}</b>
      </div>

      <div className="d-head">
        <h1 className="d-title">{c.unvan}</h1>
        <span className="d-kod">
          {c.kod}
          {c.vergiNo ? ` · VKN ${c.vergiNo}` : ''}
        </span>
        <RolRozetleri c={c} />
        {pasif && <span className="badge b-gray">Pasif</span>}
        <div className="page-actions">
          <button
            className="btn btn-danger btn-sm"
            type="button"
            disabled={kilitli}
            title={kilitNedeni}
            onClick={durumDegistir}
          >
            {pasif ? 'Aktife al' : 'Pasife al'}
          </button>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            // Bu sayfada serbest metin alanı yok: rol anahtarları ve durum
            // düğmesi anında yazıyor. Düğme yalan söylemek yerine kapalı duruyor
            // (referansın Kaydet'i yalnız toast basıyordu).
            disabled
            title="Bu sayfada düzenlenebilir alan yok — roller ve durum anında kaydedilir; bilgi alanları liste ekranındaki panelden düzenlenir"
          >
            Kaydet
          </button>
        </div>
      </div>

      <div className="kstrip">
        {/* Aylık teslimat/kabul/satın alma/sefer toplamları firma bazında
            hesaplanmıyor; K32 gereği uydurma sayı yerine boş kalıyorlar
            (gerekçe: docs/kararlar-ek.md — "Ekran 04"). */}
        <div className="k">
          <div className="k-l">Bu Ay Teslimat</div>
          <div className="k-v">—</div>
          <div className="k-s">ton hammadde</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Kabul</div>
          <div className="k-v">—</div>
          <div className="k-s">kayıt</div>
        </div>
        <div className="k">
          <div className="k-l">KM Sapması</div>
          <div className={`k-v ${sapmaVurgusu(detay.kmSapmaOrt)}`}>
            {detay.kmSapmaOrt === null ? '—' : `${sapmaMetni(detay.kmSapmaOrt)}%`}
          </div>
          <div className="k-s">karta göre · tonaj ağırlıklı</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Satın Alma</div>
          <div className="k-v">—</div>
          <div className="k-s">₺ · sarf &amp; malzeme</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Sefer</div>
          <div className="k-v">—</div>
          <div className="k-s">{c.rolNakliyeci ? 'sefer · taşıyıcı bağı yok' : 'nakliye rolü yok'}</div>
        </div>
      </div>

      <div className="d-grid">
        <div>
          {/* Kalite karnesi yalnız atık üreticisi rolünde — referans da böyle. */}
          {c.rolAtik && (
            <div className="card">
              <div className="sect-t">Hammadde Teslimatları — Substrat Bazında Kalite Karnesi</div>
              <table className="ptab">
                <thead>
                  <tr>
                    <th>Substrat</th>
                    <th className="num">Kabul</th>
                    <th className="num">Teslimat t</th>
                    <th className="num">Analiz</th>
                    <th className="num">Analiz Ort. KM</th>
                    <th className="num">Kart KM</th>
                    <th className="num">Sapma %</th>
                  </tr>
                </thead>
                <tbody>
                  {detay.kmKarnesi.length === 0 && (
                    <tr>
                      <td colSpan={7} className="cell-sub">
                        Bu firmanın firma×ürün havuzu yok — karne ilk LOT ile doğar (K27).
                      </td>
                    </tr>
                  )}
                  {detay.kmKarnesi.map((k) => (
                    <tr key={k.stokKartiId}>
                      <td>
                        <div className="cell-name">{k.stokAd}</div>
                        <div className="cell-sub">{k.stokKod}</div>
                      </td>
                      {/* Kabul adedi ve aylık tonaj firma bazında toplanmıyor. */}
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">
                        {k.analizSayisi ? k.analizSayisi : <span className="badge b-amber">yok</span>}
                      </td>
                      <td className="num">{tr(k.etkinKm, 2)}</td>
                      <td className="num">{tr(k.literaturKm, 2)}</td>
                      <td className={`num ${sapmaSinifi(k.sapmaYuzde)}`}>{sapmaMetni(k.sapmaYuzde)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pnote">
                Sapma <b>±%5</b> üstü amber, <b>±%10</b> üstü kırmızı işaretlenir — tedarikçi
                kalite takibi ve fiyat müzakeresi girdisi.
              </div>
            </div>
          )}

          <div className="card" style={c.rolAtik ? { marginTop: 8 } : undefined}>
            <div className="sect-t">İşlem Akışı — Tüm Roller</div>
            <table className="ptab">
              <thead>
                <tr>
                  <th style={{ width: 66 }}>Tarih</th>
                  <th style={{ width: 96 }}>Tip</th>
                  <th style={{ width: 96 }}>Referans</th>
                  <th>Detay</th>
                  <th className="num" style={{ width: 96 }}>Tutar</th>
                </tr>
              </thead>
              <tbody>
                {detay.islemler.length === 0 && (
                  <tr>
                    <td colSpan={5} className="cell-sub">
                      Defterde bu firmaya bağlı hareket yok.
                    </td>
                  </tr>
                )}
                {detay.islemler.map((i) => (
                  <tr key={`${i.referans}-${i.zaman}-${i.detay}`}>
                    <td className="mono" style={{ fontSize: 11 }}>{trTarih(i.zaman)}</td>
                    <td className="tur-t">{i.tip}</td>
                    <td><span className="kod">{i.referans}</span></td>
                    <td>
                      {i.detay}
                      <span className="cell-sub" style={{ marginLeft: 6 }}>
                        {i.yon < 0 ? '−' : i.yon > 0 ? '+' : ''}
                        {tr(i.miktar, 1)} {i.birim}
                      </span>
                    </td>
                    {/* K12: birim fiyat opsiyonel — fiyatsız harekette tutar yok. */}
                    <td className="num">{i.tutar === null ? '—' : `${tr(i.tutar, 0)} ₺`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="card">
            <div className="sect-t">Rol Yönetimi</div>
            <div style={{ padding: '4px 12px 8px' }}>
              <RolAnahtarlari
                c={c}
                uzun
                kilitli={kilitli}
                kilitNedeni={kilitNedeni}
                degistir={rolDegistir}
              />
            </div>
            <div className="pnote">
              Bir firma <b>üç rolü aynı anda</b> taşıyabilir; ekranlar role göre şekillenir.
            </div>
          </div>

          <div className="inst" style={{ marginTop: 8 }}>
            <div className="inst-t">Cari Ekstre — Özet (K12)</div>
            <div className="inst-row">
              <span className="inst-l">Bakiye (firma alacaklı)</span>
              {/* Cari bakiyesi fatura/ödeme defteri ister; stok defteri tek başına vermiyor. */}
              <span className="inst-v">—</span>
            </div>
            <div className="inst-row">
              <span className="inst-l">Ödeme vadesi</span>
              <span className="inst-v">{c.vadeGun === null ? '—' : `${tr(c.vadeGun, 0)} gün`}</span>
            </div>
            <div className="inst-row">
              <span className="inst-l">Defter hareketi</span>
              <span className="inst-v">{tr(detay.islemler.length, 0)}</span>
            </div>
            <div className="inst-note">
              Birim fiyat opsiyoneldir; ekstre yalnızca fiyatlı işlemlerden oluşur (K12)
            </div>
          </div>

          {c.rolNakliyeci && (
            <div className="side-card">
              <div className="sect-t">Araçlar — Bu Ay Sefer</div>
              <div className="mini-list" style={{ padding: '4px 0' }}>
                {/* §5.1 sefer kaydı nakliyeci cariye değil, yükün karşı tarafına
                    bağlanıyor; taşıyıcı kırılımı bu veriyle üretilemez. */}
                <div className="mini-li" style={{ padding: '5px 12px', color: 'var(--faint)' }}>
                  Kayıtlı araç yok
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
